import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, newIdempotencyKey } from "../lib/api";
import { formatUsd } from "../lib/format";
import { useAuth } from "../store/auth";

type Quote = {
  filledShares: number;
  unfilledShares: number;
  quantityShares?: number;
  avgPriceCents: number | null;
  bestPriceCents: number;
  slippageBps: number;
  impactCents: number;
  insufficientLiquidity: boolean;
  feeBps: number;
  feeMicro: string;
  notionalMicro: string;
  payoutMicro: string;
};

const CHIPS = [10, 20, 50, 100];

export function TradePanel({ marketId, yes, no }: { marketId: string; yes: number; no: number }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [outcome, setOutcome] = useState<"YES" | "NO">(params.get("buy") === "NO" ? "NO" : "YES");
  const [side, setSide] = useState<"BUY" | "SELL">("BUY");
  const [type, setType] = useState<"MARKET" | "LIMIT">("MARKET");
  const [price, setPrice] = useState(yes);
  const [usd, setUsd] = useState(Number(params.get("usd") || 20));
  const [qty, setQty] = useState(10);
  const [advanced, setAdvanced] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [done, setDone] = useState<null | { fills: number; remaining: number; avg?: number }>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (params.get("buy") === "NO") setOutcome("NO");
    if (params.get("buy") === "YES") setOutcome("YES");
  }, [params]);

  const px = outcome === "YES" ? yes : no;
  const simple = !advanced && side === "BUY" && type === "MARKET";
  const quoteBody = simple
    ? { outcome, side, type, amountUsd: usd }
    : { outcome, side, type, priceCents: type === "LIMIT" ? price : undefined, quantityShares: qty };

  const quoteQ = useQuery({
    queryKey: ["quote", marketId, quoteBody],
    queryFn: () =>
      api<Quote>(`/markets/${marketId}/quote`, {
        method: "POST",
        body: JSON.stringify(quoteBody),
      }),
    enabled: simple ? usd > 0 : qty > 0,
  });
  const q = quoteQ.data;
  const shares = q?.quantityShares ?? q?.filledShares ?? 0;
  const payout = shares;
  const estCost = Number(q?.notionalMicro ?? 0) / 1_000_000;

  const place = useMutation({
    mutationFn: () =>
      api<{ order: { avg_fill_cents: number }; fills: unknown[]; remaining: number }>("/orders", {
        method: "POST",
        body: JSON.stringify({
          marketId,
          ...quoteBody,
        }),
        headers: { "Idempotency-Key": newIdempotencyKey() },
      }),
    onSuccess: (d) => {
      setDone({ fills: d.fills.length, remaining: d.remaining, avg: d.order.avg_fill_cents });
      setConfirm(false);
      void qc.invalidateQueries();
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <div className="trade">
      <div className="pair">
        <button className={`btn ${outcome === "YES" ? "btn-yes" : "btn-ghost"}`} onClick={() => setOutcome("YES")}>
          Yes {yes}¢
        </button>
        <button className={`btn ${outcome === "NO" ? "btn-no" : "btn-ghost"}`} onClick={() => setOutcome("NO")}>
          No {no}¢
        </button>
      </div>
      {simple ? (
        <>
          <label className="field">
            Amount USD
            <input type="number" min={1} value={usd} onChange={(e) => setUsd(Number(e.target.value))} />
          </label>
          <div className="amt-chips">
            {CHIPS.map((n) => (
              <button key={n} type="button" className={usd === n ? "on" : ""} onClick={() => setUsd(n)}>
                ${n}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="pair">
            <button className={`btn ${side === "BUY" ? "btn-navy" : "btn-ghost"}`} onClick={() => setSide("BUY")}>Buy</button>
            <button className={`btn ${side === "SELL" ? "btn-navy" : "btn-ghost"}`} onClick={() => setSide("SELL")}>Sell</button>
          </div>
          <div className="pair">
            <button className={`btn ${type === "MARKET" ? "btn-primary" : "btn-ghost"}`} onClick={() => setType("MARKET")}>Market</button>
            <button className={`btn ${type === "LIMIT" ? "btn-primary" : "btn-ghost"}`} onClick={() => setType("LIMIT")}>Limit</button>
          </div>
          {type === "LIMIT" && (
            <label className="field">
              Limit price (¢)
              <input type="number" min={1} max={99} value={price} onChange={(e) => setPrice(Number(e.target.value))} />
            </label>
          )}
          <label className="field">
            Contracts
            <input type="number" min={1} value={qty} onChange={(e) => setQty(Number(e.target.value))} />
          </label>
        </>
      )}
      <div className="quote">
        <div><span>Chance</span><strong>{px}%</strong></div>
        <div><span>Est. fill</span><strong>{q?.avgPriceCents ? `${q.avgPriceCents}¢` : "—"}</strong></div>
        <div><span>Contracts</span><strong>{shares || "—"}</strong></div>
        <div><span>You pay</span><strong>{formatUsd(q?.notionalMicro ?? 0)}</strong></div>
        <div><span>Fee</span><strong>{formatUsd(q?.feeMicro ?? 0)}</strong></div>
        <div><span>If {outcome} wins</span><strong>${payout.toFixed(2)}</strong></div>
        <div><span>Potential profit</span><strong>${(payout - estCost).toFixed(2)}</strong></div>
        {q?.insufficientLiquidity && (
          <p style={{ color: "var(--danger)", margin: "8px 0 0" }}>
            Only part of that size is resting on the book. The rest will not fill.
          </p>
        )}
      </div>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdvanced((v) => !v)}>
        {advanced ? "Simple $ ticket" : "Limit / sell"}
      </button>
      {err && <p style={{ color: "var(--danger)" }}>{err}</p>}
      {!user ? (
        <button className="btn btn-primary btn-block" onClick={() => nav("/login")}>Sign in to trade</button>
      ) : !confirm ? (
        <button className={`btn btn-block ${outcome === "YES" ? "btn-yes" : "btn-no"}`} onClick={() => { setConfirm(true); setErr(""); }}>
          {side === "BUY" ? "Buy" : "Sell"} {outcome} · {simple ? `$${usd}` : `${qty} contracts`}
        </button>
      ) : (
        <div>
          <p>Confirm buy {shares} {outcome} {simple ? `for about $${usd}` : ""}.</p>
          <button className="btn btn-navy btn-block" disabled={place.isPending} onClick={() => place.mutate()}>
            {place.isPending ? "Submitting…" : "Confirm trade"}
          </button>
          <button className="btn btn-ghost btn-block" style={{ marginTop: 8 }} onClick={() => setConfirm(false)}>Back</button>
        </div>
      )}
      {done && (
        <div className="notice">
          <strong>You’re in.</strong>
          <p>{done.fills ? `Filled at ${done.avg ?? "—"}¢.` : "Resting on the book."}</p>
          <button className="btn btn-ghost" onClick={() => nav("/portfolio")}>View position</button>
        </div>
      )}
    </div>
  );
}
